/**
 * Inline add/edit helpers for the Product Information table view.
 *
 * A draft row is a plain object of the core create-page fields. It is validated
 * against the same required set the Create Product page enforces, then mapped to
 * the payload shape the existing addProduct / updateProduct actions accept.
 */

import type { ProductComponentList } from '../../../../Components/ProductInformation/ProductComponentModal';
import { DynamicColumn, MultiCustomAttribute, TableRow } from './types';
import { multiAttributeAdmits } from '../CreateProduct/customAttributeScope';
import { PRODUCT_TYPE_CHANGE_CONFIRMED_FLAG } from '../CreateProduct/productTypeChange';

/** Required core fields, mirroring CreateProduct's requiredField list. */
export const REQUIRED_FIELDS: string[] = ['number', 'description', 'brandId', 'supplierType', 'catOneId', 'catTwoId'];

/** Every draft (unsaved) row id starts with this; the rest is a per-page sequence. */
export const DRAFT_ID_PREFIX = '__new__';

/** Empty draft row for a brand-new inline product (all create-page fields). */
export const emptyDraftRow = (id: string = DRAFT_ID_PREFIX): TableRow => ({
  _id: id,
  __isDraft: true,
  productNumberType: 'Manual',
  number: '',
  description: '',
  tagDescription: false,
  brandId: '',
  subBrandId: '',
  isLicensedProduct: false,
  licensorId: '',
  licenseId: '',
  likenessId: '',
  supplierType: '',
  catOneId: '',
  catTwoId: '',
  catThreeId: '',
  catFourId: '',
  seasonId: '',
  month: '',
  year: '',
  sizeRangeId: [],
  sizeRangeSizes: [],
  legLengths: [],
  colourLibraries: [],
  suppliers: [],
  supplierFactories: [],
  visibleToSupplier: true,
  transport: '',
  productTypeProcesses: [],
  status: '',
  tags: '',
  image: '',
  fullImg: '',
});

/**
 * Validate a draft row against the create page's required rules. Licensor stays optional even
 * when the licensed toggle is on, matching the Edit Product page.
 * @param draftRow the inline row being added/edited
 * @returns field -> error message key
 */
export const validateDraft = (draftRow: TableRow | null): Record<string, string> => {
  const errors: Record<string, string> = {};
  const isMissing = (value: unknown) => value === undefined || value === null || String(value).trim() === '';
  REQUIRED_FIELDS.forEach((field) => {
    if (isMissing(draftRow?.[field])) errors[field] = 'FILTER.REQUIRED_FIELD';
  });
  return errors;
};

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const OBJECT_ID_PATTERN = /^[a-f\d]{24}$/i;

const isObjectId = (value: unknown): boolean => typeof value === 'string' && OBJECT_ID_PATTERN.test(value);

const refId = (value: unknown): string | null => (isObjectId(value) ? (value as string) : null);

/**
 * Array counterpart of {@link refId}: keep only entries that are real ObjectIds.
 *
 * The server casts these arrays with `new ObjectId(...)`, so one display name reaching it rejects
 * the whole product update. Not for `soldCountries`, which holds slugs rather than ids.
 * @param value the row value for an id-array field
 * @returns the entries that are real ObjectIds
 */
const refIdArray = (value: unknown): string[] => asArray(value).filter(isObjectId) as string[];

/** Collect mca_<colId> row keys into the multiCustomAttribute payload shape. */
const collectMultiCustom = (row: TableRow): Array<{ colId: string; valId: string[] }> =>
  Object.keys(row)
    .filter((key) => key.startsWith('mca_') && !key.endsWith('__label'))
    .map((key) => ({ colId: key.slice(4), valId: refIdArray(row[key]) }))
    .filter((entry) => isObjectId(entry.colId) && entry.valId.length > 0);

/**
 * Resolve a dynamic-attribute entry's id (mirrors rowMapper's dynamicAttrId): the id may live
 * on `_id` directly or under a populated/plain `colId`.
 */
const dynAttrId = (entry: DynamicColumn): string => {
  if (entry?._id) return String(entry._id);
  const colId = entry?.colId as { _id?: string } | string | undefined;
  if (colId && typeof colId === 'object' && colId._id) return String(colId._id);
  if (typeof colId === 'string') return colId;
  return '';
};

/** Attribute ids whose value is an option id (valueId), not free text — dropdown/radio. */
const OPTION_TYPES = new Set(['dropdown', 'radio-button']);

/**
 * Collect dyn_<colId> row keys into the dynamicColumn payload shape the create/update
 * endpoints expect ({ colId, valueId, valueText }), mirroring ProductModal's dynamicColumn
 * mapping. The custom-attribute master (`attributes`) tells us each attribute's type:
 * dropdown/radio store the selected option id under dyn_<id> (→ valueId); text/date store
 * their string (→ valueText). Attributes not in the master default to valueText.
 */
const collectDynamic = (row: TableRow, attributes: DynamicColumn[]): Array<{ colId: string; valueId: string | null; valueText: string }> => {
  const typeById = new Map<string, string | undefined>();
  attributes.forEach((attribute) => typeById.set(dynAttrId(attribute), attribute.type));
  return Object.keys(row)
    .filter((key) => key.startsWith('dyn_') && !key.endsWith('__label'))
    .map((key) => {
      const colId = key.slice(4);
      const value = row[key];
      const stringValue = value === undefined || value === null ? '' : String(value);
      const isOption = OPTION_TYPES.has(typeById.get(colId) ?? '');
      const isReference = isOption && isObjectId(stringValue);
      return {
        colId,
        valueId: isReference ? stringValue : null,
        valueText: isReference ? '' : stringValue,
      };
    })
    .filter((entry) => isObjectId(entry.colId) && (entry.valueId || entry.valueText));
};

/**
 * Backend sentinel meaning "leave the stored image alone". The update endpoint re-uploads
 * `image` / `fullImg` whenever they differ from this, so a row the user never re-cropped must
 * send the sentinel instead of echoing back the presigned URL the thumbnail was rendered from —
 * echoing it re-saves that URL as the product's image.
 */
const IMAGE_UNCHANGED = 'DoNotChange';

/**
 * Whether a row carries a newly cropped image. ImageCell writes the crop result back into the
 * row as a `data:` URI; any other value is an image the product already has.
 */
const isFreshImage = (value: unknown): boolean => typeof value === 'string' && value.startsWith('data:');

/**
 * Reduce a populated reference (or an already-bare id) to its plain id string. The detail
 * response projects grading groups as `{ value, label, … }` rather than `{ _id, name }`, so
 * `value` is accepted as an id too — mirroring the Create Product page's `gradingGroups.map(ele => ele.value)`.
 */
const toId = (value: unknown): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  const entry = value as { _id?: unknown; value?: unknown };
  const id = entry._id ?? entry.value;
  return id ? String(id) : '';
};

/** Array counterpart of {@link toId}, dropping entries that resolve to nothing. */
const toIdList = (value: unknown): string[] => asArray(value).map(toId).filter(Boolean);

/**
 * Detail-response keys that must not be echoed back on save: `reOrders` is a large derived array
 * the endpoint ignores, cat3Data / cat4Data are view-only helpers the Create Product page
 * deletes from its own save object for the same reason, and packingMethod / otherPackingMethod are
 * retired fields now carried by custom attributes.
 */
const NON_PAYLOAD_KEYS = ['reOrders', 'cat3Data', 'cat4Data', 'packingMethod', 'otherPackingMethod'];

const isInternalRowField = (field: string): boolean => field.startsWith('__') || field.endsWith('__label');

const valuesMatch = (left: unknown, right: unknown): boolean => {
  if (Object.is(left, right)) return true;
  if (left === undefined || right === undefined || left === null || right === null) return left === right;
  if (typeof left !== 'object' || typeof right !== 'object') return false;
  return JSON.stringify(left) === JSON.stringify(right);
};

/** Editable row fields whose current value differs from the edit baseline. */
export const changedFields = (row: TableRow, baseline: TableRow | undefined): string[] => {
  const fields = new Set([...Object.keys(row), ...Object.keys(baseline ?? {})]);
  return [...fields].filter((field) => !isInternalRowField(field) && !valuesMatch(row[field], baseline?.[field]));
};

/**
 * Map a draft/edited row to the payload accepted by addProduct / updateProduct.
 *
 * An EDIT starts from the full product detail (`__product` — the same response the Create Product
 * page edits from) and layers the inline row's values over it, mirroring that page's
 * `{ ...editedProductData, ...overrides }` save object. This matters because the update endpoint
 * `$set`s every field it reads off the request body: a key the payload omits arrives as
 * `undefined`, which the Mongo driver serialises to `null` — silently clearing grading groups,
 * product identifiers, factory countries, the size-range name snapshot and the approval/shipping
 * dates. An ADD has no `__product`, so it builds from the row alone.
 *
 * A DUPLICATE draft (`__isDraft` with a `__product`) is built like an edit — the source detail
 * underneath, the row's values on top — so the copy keeps everything the row never renders, but
 * without the source's identity: its `_id` never travels and its cluster membership is replaced by
 * the draft's own Cluster toggle.
 * @param row the inline row to persist
 * @param customAttributes the single custom-attribute master (supplies each dyn_ attribute's
 *   type so dropdown/radio values save as valueId and text/date as valueText)
 * @returns the create/update payload
 */
export const buildProductPayload = (row: TableRow, customAttributes: DynamicColumn[] = []): Record<string, unknown> => {
  const source = row.__product as unknown as Record<string, unknown> | undefined;
  const preserved: Record<string, unknown> = { ...(source || {}) };
  NON_PAYLOAD_KEYS.forEach((key) => delete preserved[key]);
  /*
   * The image cell's Delete flags the row rather than mutating anything: the removal travels with
   * the save like every other edit, so Cancel puts the picture back. It has to beat the
   * `keepsStoredImage` sentinel below, which would otherwise answer "leave the stored image alone"
   * for the now-empty value and quietly undo the deletion.
   */
  const removesStoredImage = Boolean(row.__imageRemoved);
  const isLibraryAsset = Boolean(row.isUploadAsset);
  const keepsStoredImage = Boolean(source) && !removesStoredImage && !isLibraryAsset && !isFreshImage(row.image);
  /*
   * `sizeRanges` is the stored snapshot of grading size NAMES, which the product card prints
   * verbatim. Falling back to `sizeRangeId` put ObjectIds in it and the card showed them raw.
   * With no stored snapshot send an empty array, exactly as the Create Product page does
   * (`resolveSizeRangesSnapshot` — `editedProductData?.sizeRanges ?? []`).
   */
  const sizeRangesSnapshot = Array.isArray(source?.sizeRanges) ? (source?.sizeRanges as unknown[]) : [];
  const rowImage = typeof row.image === 'string' ? row.image : '';
  const rowFullImg = typeof row.fullImg === 'string' ? row.fullImg : '';

  const payload: Record<string, unknown> = {
    ...preserved,
    productNumberType: row.productNumberType === 'Auto' || row.productNumberType === true,
    counterNo: (row.counterNo as string) || (source?.counterNo as string) || '',
    number: row.number,
    description: row.description,
    tagDescription: Boolean(row.tagDescription ?? source?.tagDescription),
    /* Set by the image cell's "Add to Design Detail" checkbox; the same field the form sends. */
    isIncludeDesign: Boolean(row.isIncludeDesign),
    brandId: refId(row.brandId),
    subBrandId: refId(row.subBrandId),
    isLicensedProduct: row.isLicensedProduct || false,
    licensorId: refId(row.licensorId),
    licenseId: refId(row.licenseId),
    likenessId: refId(row.likenessId),
    supplierType: refId(row.supplierType),
    [PRODUCT_TYPE_CHANGE_CONFIRMED_FLAG]: row[PRODUCT_TYPE_CHANGE_CONFIRMED_FLAG] === true,
    catOneId: refId(row.catOneId),
    catTwoId: refId(row.catTwoId),
    catThreeId: refId(row.catThreeId),
    catFourId: refId(row.catFourId),
    seasonId: refId(row.seasonId),
    month: row.month || null,
    year: row.year || null,
    sizeRangeId: refIdArray(row.sizeRangeId),
    sizeRangeSizes: refIdArray(row.sizeRangeSizes),
    soldCountries: asArray(row.soldCountries),
    legLengths: JSON.stringify(asArray(row.legLengths)),
    colourLibraries: JSON.stringify(asArray(row.colourLibraries)),
    sizeRanges: JSON.stringify(sizeRangesSnapshot),
    suppliers: JSON.stringify(asArray(row.suppliers)),
    followers: JSON.stringify(asArray(row.suppliers)),
    supplierFactories: asArray(row.supplierFactories),
    factoryCountry: Array.isArray(row.factoryCountry) ? row.factoryCountry : preserved.factoryCountry ?? [],
    visibleToSupplier: row.visibleToSupplier !== false,
    /* Transport holds a `transportArray` slug (e.g. `ship_container_ship`), not an ObjectId, so it
       is sent as-is like the Create/Edit Product page does — `refId` would null every value. */
    transport: (row.transport as string) || null,
    productTypeProcesses: asArray(row.productTypeProcesses),
    status: refId(row.status),
    tags: typeof row.tags === 'string' && row.tags.trim() ? row.tags.split(',').map((tag) => tag.trim()) : [],
    multiCustomAttribute: collectMultiCustom(row),
    dynamicColumn: collectDynamic(row, customAttributes),
    image: keepsStoredImage ? IMAGE_UNCHANGED : rowImage,
    fullImg: keepsStoredImage ? IMAGE_UNCHANGED : rowFullImg,
    assetId: isLibraryAsset ? (row.assetId as string) || null : null,
    isAddToAssetLibrary: !isLibraryAsset && row.isAddToAssetLibrary === true,
    removeImage: removesStoredImage,
    additionalVariables: 'additionalVariables' in row ? row.additionalVariables ?? null : source?.additionalVariables ?? null,
  };

  /**
   * Cluster intent lives on the row for a duplicate draft: the toggle sets `isReorder`, and
   * `parentDevOrderId` is the product the copy was made from. `parentDevId` names that same product
   * for the duplicate endpoint, which walks it up to the cluster's real parent so a cluster stays
   * one level deep.
   */
  const clustersOntoSource = Boolean(row.isReorder && row.parentDevOrderId);
  if (clustersOntoSource) {
    payload.isReorder = true;
    payload.parentDevOrderId = row.parentDevOrderId;
    payload.parentDevId = row.parentDevOrderId;
  }

  if (!source) return payload;

  /**
   * Grading groups arrive populated and the endpoint casts ids itself, so they are reduced to plain
   * ids — guarded so a source that never carried the field (the list item behind a bulk-edit row,
   * as opposed to the full detail) cannot overwrite it with a derived-from-nothing value.
   */
  if ('gradingGroups' in source) payload.gradingGroups = toIdList(source.gradingGroups);

  /**
   * A duplicate draft drops the source's identity: the `_id` must not reach the endpoint, and the
   * source's own cluster link gives way to the toggle above — otherwise copying a cluster member
   * with Cluster unticked would re-link the copy to that member's parent.
   */
  if (row.__isDraft) {
    delete payload._id;
    if (!clustersOntoSource) {
      delete payload.isReorder;
      delete payload.parentDevOrderId;
    }
    return payload;
  }

  /**
   * Edit-only: `parentDevOrderId` is only meaningful on a reorder — the Create Product page deletes
   * it otherwise, and leaving it in would re-link a standalone product to itself.
   */
  if ('isReorder' in source) payload.isReorder = Boolean(source.isReorder);
  if (payload.isReorder) payload.parentDevOrderId = source.parentDevOrderId ?? row._id;
  else delete payload.parentDevOrderId;
  return payload;
};

/**
 * Custom-attribute text values that break their configured character limit.
 *
 * The Create Product page enforces this three times over — a `maxLength` on the input, a red
 * message under it, and a save blocker in `handleAddUpdate`. The grid enforced it nowhere, so
 * 5000 characters could be typed into a 100-character attribute and saved. `isExceedable` only
 * governs whether the input caps typing; an over-limit value is rejected on save either way.
 * @param row the inline row being edited
 * @param customAttributes the single custom-attribute master
 * @returns `dyn_<attrId>` -> the limit it broke, empty when every value fits
 */
export const overCharLimitFields = (row: TableRow | null, customAttributes: DynamicColumn[] = []): Record<string, number> => {
  const broken: Record<string, number> = {};
  customAttributes.forEach((attribute) => {
    if (attribute.type !== 'text' || !attribute.isActive) return;
    const limit = attribute.textFieldConfig?.charLimit;
    if (!limit) return;
    const key = `dyn_${dynAttrId(attribute)}`;
    if (String(row?.[key] ?? '').length > limit) broken[key] = limit;
  });
  return broken;
};

/**
 * Every component status carries over to a copy made inline: the same selection the
 * ProductComponentModal opens with, since the table view offers no room for that checklist.
 */
export const CARRY_ALL_COMPONENTS: ProductComponentList = {
  bomColorApproval: true,
  bomApproval: true,
  careReport: true,
  testReport: true,
  sample: true,
  documents: true,
  selectAll: true,
};

/**
 * Map a duplicate draft to the payload the duplicate endpoint accepts: the product payload plus the
 * per-copy flags the Create Product page sends — `isActive` false for a copy, no image removal (the
 * server copies the source's stored image and full image under the new product), and the component
 * statuses to keep.
 * @param row the duplicate draft, carrying its source under `__product`
 * @param customAttributes the single custom-attribute master, as for {@link buildProductPayload}
 * @returns the duplicate payload
 */
export const buildDuplicatePayload = (row: TableRow, customAttributes: DynamicColumn[] = []): Record<string, unknown> => ({
  ...buildProductPayload(row, customAttributes),
  isActive: false,
  removeImage: false,
  removeApproval: CARRY_ALL_COMPONENTS,
});

/**
 * Validate every open inline row with the same rules a single row uses. Rows that pass are
 * omitted entirely, so an empty result means the whole set is safe to submit.
 * @param rows rowId -> row being edited
 * @returns rowId -> (field -> error message key), only for rows with errors
 */
export const validateInlineRows = (rows: Record<string, TableRow>): Record<string, Record<string, string>> => {
  const errorsByRow: Record<string, Record<string, string>> = {};
  Object.entries(rows).forEach(([rowId, row]) => {
    const errors = validateDraft(row);
    if (Object.keys(errors).length) errorsByRow[rowId] = errors;
  });
  return errorsByRow;
};

/**
 * Map every open inline row to the bulk-update wire shape: the single-product payload plus the
 * `devOrderId` the server needs to address each row.
 * @param rows rowId -> row being edited
 * @param customAttributes custom-attribute master, for the dynamic column mapping
 * @returns one payload entry per row
 */
export const buildBulkUpdatePayload = (
  rows: Record<string, TableRow>,
  customAttributes: DynamicColumn[] = [],
): Array<Record<string, unknown>> =>
  Object.entries(rows).map(([rowId, row]) => ({
    devOrderId: rowId,
    ...buildProductPayload(row, customAttributes),
  }));

const PAYLOAD_COMPANIONS: Record<string, string[]> = {
  suppliers: ['followers'],
  image: ['fullImg', 'removeImage', 'assetId', 'isAddToAssetLibrary', 'isIncludeDesign'],
  fullImg: ['image', 'removeImage', 'assetId', 'isAddToAssetLibrary', 'isIncludeDesign'],
  sizeRangeId: ['sizeRangeSizes'],
  supplierFactories: ['factoryCountry'],
};

/** Build the partial bulk wire payload for one edited product. */
export const buildDirtyPayload = (
  row: TableRow,
  baseline: TableRow | undefined,
  customAttributes: DynamicColumn[] = [],
): Record<string, unknown> => {
  const fullPayload = buildProductPayload(row, customAttributes);
  const fields = new Set(changedFields(row, baseline));
  if ([...fields].some((field) => field.startsWith('dyn_'))) fields.add('dynamicColumn');
  if ([...fields].some((field) => field.startsWith('mca_'))) fields.add('multiCustomAttribute');
  const payloadKeys = new Set<string>();
  fields.forEach((field) => {
    payloadKeys.add(field);
    (PAYLOAD_COMPANIONS[field] ?? []).forEach((companion) => payloadKeys.add(companion));
  });
  return Object.fromEntries([
    ['devOrderId', String(row._id)],
    ...[...payloadKeys]
      .filter((key) => Object.prototype.hasOwnProperty.call(fullPayload, key))
      .map((key) => [key, fullPayload[key]]),
  ]);
};

/**
 * Whether a scope list names the given id.
 * @param scope the attribute's `brand` or `productType` list
 * @param id the id to look for
 * @returns true when the list names that id
 */
export const scopeHasId = (scope: unknown, id: string): boolean =>
  Array.isArray(scope) && Boolean(id) && scope.some((entry) => String((entry as { _id?: string })?._id ?? entry) === id);

/**
 * Whether a SINGLE custom attribute's scope list admits the given id, mirroring the server's scope
 * query (master.service.js `getCustomAttribute`): a list matches when it names the id, or when it
 * declares no restriction at all — an empty array, or the field absent entirely.
 * @param scope the attribute's own `brand` or `productType` list
 * @param id the row's brand id or product type id
 * @returns true when the attribute applies to that id
 */
export const scopeAllows = (scope: DynamicColumn['brand'] | DynamicColumn['productType'], id: string): boolean =>
  !Array.isArray(scope) || scope.length === 0 ? true : scopeHasId(scope, id);

/**
 * Clear custom-attribute values the row's new brand or product type no longer admits.
 *
 * The columns already disable themselves per row when out of scope, but a disabled cell still
 * holds its value and `collectDynamic` still sends it — so a product moved to another brand kept
 * saving the old brand's attributes. This runs from the change cascade, on the row as it is after
 * the new value landed, and blanks both the value and its display label.
 *
 * @param row the row after `field` changed
 * @param field the field that changed
 * @param attributes the custom-attribute master
 * @returns the row with out-of-scope dyn_ values cleared, or the same row for any other field
 */
export const clearOutOfScopeDynamicValues = (row: TableRow, field: string, attributes: readonly DynamicColumn[] = []): TableRow => {
  if (field !== 'brandId' && field !== 'supplierType') return row;
  const next: TableRow = { ...row };
  attributes.forEach((attribute) => {
    const admits =
      field === 'brandId' ? scopeAllows(attribute.brand, String(row.brandId ?? '')) : scopeAllows(attribute.productType, String(row.supplierType ?? ''));
    if (admits) return;
    const key = `dyn_${dynAttrId(attribute)}`;
    if (key in next) next[key] = '';
    if (`${key}__label` in next) next[`${key}__label`] = '';
  });
  return next;
};

/**
 * Clear the multi-select custom-attribute values (`mca_<id>`) the row's Product Type does not offer.
 * Unlike a single attribute, a multi-select attribute has no brand and is offered only to the types it
 * lists, so an attribute listing none is offered to none, and a row with no type is offered none.
 * @param row the row after its Product Type changed
 * @param multiAttributes the multi-select custom-attribute master
 * @returns a copy of the row with the values and display labels of unavailable attributes cleared
 */
export const clearOutOfScopeMultiValues = (row: TableRow, multiAttributes: readonly MultiCustomAttribute[] = []): TableRow => {
  const next: TableRow = { ...row };
  multiAttributes.forEach((attribute) => {
    if (multiAttributeAdmits(attribute, String(row.supplierType ?? ''))) return;
    const key = `mca_${attribute._id}`;
    if (key in next) next[key] = [];
    if (`${key}__label` in next) next[`${key}__label`] = '';
  });
  return next;
};

/**
 * Clear what a Product Type change invalidates beyond suppliers, mirroring the Edit Product screen: the
 * Product Type Processes always, and the whole category chain when categories are scoped to the product type.
 * @param row the row after its Product Type changed
 * @param categoriesFollowProductType the tenant setting that scopes Category 1 to the product type
 * @returns a copy of the row with those fields cleared
 */
export const resetProductTypeScopedFields = (row: TableRow, categoriesFollowProductType: boolean): TableRow => ({
  ...row,
  productTypeProcesses: [],
  ...(categoriesFollowProductType ? { catOneId: '', catTwoId: '', catThreeId: '', catFourId: '' } : {}),
});

/**
 * Whether an edit actually moved the row to a different Product Type. Re-picking, pasting or fill-dragging the
 * value a row already holds writes the field again but changes nothing, and must not clear what depends on it.
 * @param previous the row before the edit
 * @param next the row after the edit
 * @returns true when the Product Type differs
 */
export const isProductTypeChanged = (previous: TableRow | undefined, next: TableRow): boolean =>
  String(previous?.supplierType ?? '') !== String(next.supplierType ?? '');

/**
 * Whether a row id names a draft (unsaved) row rather than a stored product.
 * @param id a grid row id
 * @returns true for a draft id
 */
export const isDraftId = (id: unknown): boolean => typeof id === 'string' && id.startsWith(DRAFT_ID_PREFIX);

/**
 * Fields a copied draft must not inherit: the number and counter must be unique per product, the
 * image belongs to the row it was cropped on, and the cluster link marks a duplicate, not a copy.
 */
const DRAFT_COPY_RESET: Partial<TableRow> = {
  number: '',
  counterNo: '',
  image: '',
  fullImg: '',
  assetId: '',
  isUploadAsset: false,
  __imageRemoved: false,
  parentDevOrderId: undefined,
  isReorder: false,
};

/**
 * Copy a draft into a new draft, the way Placeholder Management's row "+" does.
 * @param row the draft being copied
 * @param id the new draft's id
 * @returns the copy
 */
export const copyDraftRow = (row: TableRow, id: string): TableRow => ({ ...row, ...DRAFT_COPY_RESET, _id: id, __isDraft: true });

/**
 * Map draft rows to the bulk-create wire shape: the single-create payload plus the draft's id, which
 * the server echoes back so each outcome lands on the right row.
 * @param drafts the draft rows to create
 * @param customAttributes custom-attribute master, for the dynamic column mapping
 * @returns one payload entry per draft
 */
export const buildBulkCreatePayload = (drafts: TableRow[], customAttributes: DynamicColumn[] = []): Array<Record<string, unknown>> =>
  drafts.map((row) => ({ clientRowId: row._id, ...buildProductPayload(row, customAttributes) }));
